import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import type { SelectedFixtureData } from '../types';

interface OverUnderOptionsGeneratorProps {
  selectedFixtureData: SelectedFixtureData | null;
  teamMap: Map<string, { name: string; abbreviation?: string }>;
}

export function OverUnderOptionsGenerator({
  selectedFixtureData,
  teamMap,
}: OverUnderOptionsGeneratorProps) {
  const { values, setFieldValue } = useFormikContext<QuestionFormValues>();
  const prevQuestionTypeRef = useRef(values.question_type);

  // Extract config values outside useEffect so they can be used in dependency array
  const config = values.question_metadata?.grading?.config as
    | {
        threshold?: number;
        playerId?: string;
        playerName?: string;
        playerPosition?: string;
        teamId?: string | number;
        teamName?: string;
        teamShortcode?: string;
        teamLogoUrl?: string;
      }
    | undefined;
  const threshold = config?.threshold;
  const playerId = config?.playerId;
  const playerName = config?.playerName;
  const playerPosition = config?.playerPosition;
  const teamId = config?.teamId;
  const teamName = config?.teamName;
  const teamShortcode = config?.teamShortcode;
  const teamLogoUrl = config?.teamLogoUrl;

  const isOverUnderType =
    values.question_type === 'over_under' ||
    values.question_type === ('player_over_under' as any) ||
    values.question_type === ('team_over_under' as any);
  const isPlayerOverUnder = values.question_type === ('player_over_under' as any);
  const isTeamOverUnder = values.question_type === ('team_over_under' as any);

  useEffect(() => {
    const currentType = values.question_type;
    const prevType = prevQuestionTypeRef.current;

    // Auto-generate Over/Under options when switching to over/under type
    if (isOverUnderType && prevType !== currentType) {
      // Build fixtureData if fixture is selected (includes abbreviations for sportMatchData logos)
      const fixtureData = selectedFixtureData?.fixture
        ? (() => {
            const homeTeam = teamMap.get(selectedFixtureData.fixture.homeTeamId || '');
            const awayTeam = teamMap.get(selectedFixtureData.fixture.awayTeamId || '');
            return {
              fixtureId: selectedFixtureData.fixtureId,
              homeTeamId: selectedFixtureData.fixture.homeTeamId || '',
              awayTeamId: selectedFixtureData.fixture.awayTeamId || '',
              homeTeamName: selectedFixtureData.fixture.homeTeamName || '',
              awayTeamName: selectedFixtureData.fixture.awayTeamName || '',
              homeTeamAbbreviation: homeTeam?.abbreviation,
              awayTeamAbbreviation: awayTeam?.abbreviation,
              scheduledDate: selectedFixtureData.fixture.scheduledDate || new Date().toISOString(),
              status: (selectedFixtureData.fixture.status || 'scheduled') as
                | 'scheduled'
                | 'live'
                | 'finished'
                | 'postponed'
                | 'cancelled',
            };
          })()
        : undefined;

      // Determine teamId for player_over_under (from fixture if available, otherwise from config)
      const resolvedTeamId =
        isPlayerOverUnder && selectedFixtureData?.fixture && teamName
          ? // For player over/under, match teamName to fixture teams to get teamId
            selectedFixtureData.fixture.homeTeamName === teamName
            ? selectedFixtureData.fixture.homeTeamId
            : selectedFixtureData.fixture.awayTeamName === teamName
              ? selectedFixtureData.fixture.awayTeamId
              : teamId || selectedFixtureData.fixture.homeTeamId
          : teamId;

      const baseGradingData: any = {
        threshold,
        fixtureData,
      };

      if (isPlayerOverUnder) {
        baseGradingData.playerId = playerId;
        baseGradingData.playerName = playerName;
        baseGradingData.playerPosition = playerPosition;
        baseGradingData.teamId = resolvedTeamId;
        baseGradingData.teamName = teamName;
        baseGradingData.teamShortcode = teamShortcode;
      } else if (isTeamOverUnder) {
        baseGradingData.teamId = teamId;
        baseGradingData.teamName = teamName;
        baseGradingData.teamShortcode = teamShortcode;
      } else {
        // Legacy over_under type - default to player
        baseGradingData.playerId = playerId;
        baseGradingData.playerName = playerName;
        baseGradingData.playerPosition = playerPosition;
        baseGradingData.teamId = resolvedTeamId;
        baseGradingData.teamName = teamName;
        baseGradingData.teamShortcode = teamShortcode;
      }

      // For team over/under, include team logo on both options (frontend displays via option_image)
      const teamOverUnderImageFields =
        isTeamOverUnder && teamLogoUrl
          ? { option_image_en: teamLogoUrl, option_image_fr: teamLogoUrl }
          : {};

      // For player over/under (and legacy over_under), include jersey on both options (frontend displays via option_third_image/option_image)
      const jerseyUrl = values.question_image_en;
      const playerOverUnderImageFields =
        (isPlayerOverUnder || values.question_type === 'over_under') && jerseyUrl
          ? {
              option_image_en: jerseyUrl,
              option_image_fr: values.question_image_fr || jerseyUrl,
              third_image_en: teamLogoUrl,
              third_image_fr: values.question_image_fr || teamLogoUrl,
            }
          : {};

      setFieldValue('options', [
        {
          option_text_en: 'Over',
          option_text_fr: 'Plus',
          is_correct: false,
          display_order: 0,
          grading_data: { ...baseGradingData, comparison: 'over' },
          ...teamOverUnderImageFields,
          ...playerOverUnderImageFields,
        },
        {
          option_text_en: 'Under',
          option_text_fr: 'Moins',
          is_correct: false,
          display_order: 1,
          grading_data: { ...baseGradingData, comparison: 'under' },
          ...teamOverUnderImageFields,
          ...playerOverUnderImageFields,
        },
      ]);
    }

    // Update grading_data when any relevant field changes
    if (isOverUnderType && values.options.length === 2) {
      // Build fixtureData for comparison (includes abbreviations for sportMatchData)
      const currentFixtureData = selectedFixtureData?.fixture
        ? (() => {
            const homeTeam = teamMap.get(selectedFixtureData.fixture.homeTeamId || '');
            const awayTeam = teamMap.get(selectedFixtureData.fixture.awayTeamId || '');
            return {
              fixtureId: selectedFixtureData.fixtureId,
              homeTeamId: selectedFixtureData.fixture.homeTeamId || '',
              awayTeamId: selectedFixtureData.fixture.awayTeamId || '',
              homeTeamName: selectedFixtureData.fixture.homeTeamName || '',
              awayTeamName: selectedFixtureData.fixture.awayTeamName || '',
              homeTeamAbbreviation: homeTeam?.abbreviation,
              awayTeamAbbreviation: awayTeam?.abbreviation,
              scheduledDate: selectedFixtureData.fixture.scheduledDate || new Date().toISOString(),
              status: (selectedFixtureData.fixture.status || 'scheduled') as
                | 'scheduled'
                | 'live'
                | 'finished'
                | 'postponed'
                | 'cancelled',
            };
          })()
        : undefined;

      const resolvedTeamId =
        isPlayerOverUnder && selectedFixtureData?.fixture && teamName
          ? selectedFixtureData.fixture.homeTeamName === teamName
            ? selectedFixtureData.fixture.homeTeamId
            : selectedFixtureData.fixture.awayTeamName === teamName
              ? selectedFixtureData.fixture.awayTeamId
              : teamId || selectedFixtureData.fixture.homeTeamId
          : teamId;

      const needsUpdate =
        values.options[0].grading_data?.threshold !== threshold ||
        JSON.stringify(values.options[0].grading_data?.fixtureData) !==
          JSON.stringify(currentFixtureData) ||
        (isPlayerOverUnder &&
          (values.options[0].grading_data?.playerId !== playerId ||
            values.options[1].grading_data?.playerId !== playerId ||
            values.options[0].grading_data?.playerName !== playerName ||
            values.options[1].grading_data?.playerName !== playerName ||
            values.options[0].grading_data?.playerPosition !== playerPosition ||
            values.options[1].grading_data?.playerPosition !== playerPosition ||
            values.options[0].grading_data?.teamId !== resolvedTeamId ||
            values.options[1].grading_data?.teamId !== resolvedTeamId ||
            values.options[0].grading_data?.teamName !== teamName ||
            values.options[1].grading_data?.teamName !== teamName ||
            values.options[0].grading_data?.teamShortcode !== teamShortcode ||
            values.options[1].grading_data?.teamShortcode !== teamShortcode ||
            values.options[0].option_image_en !== values.question_image_en ||
            values.options[0].third_image_en !== values.question_image_en)) ||
        (isTeamOverUnder &&
          (values.options[0].grading_data?.teamId !== teamId ||
            values.options[1].grading_data?.teamId !== teamId ||
            values.options[0].grading_data?.teamName !== teamName ||
            values.options[1].grading_data?.teamName !== teamName ||
            values.options[0].grading_data?.teamShortcode !== teamShortcode ||
            values.options[1].grading_data?.teamShortcode !== teamShortcode ||
            values.options[0].option_image_en !== teamLogoUrl ||
            values.options[1].option_image_en !== teamLogoUrl)) ||
        (!isPlayerOverUnder &&
          !isTeamOverUnder &&
          (values.options[0].grading_data?.playerId !== playerId ||
            values.options[1].grading_data?.playerId !== playerId ||
            values.options[0].option_image_en !== values.question_image_en ||
            values.options[0].third_image_en !== values.question_image_en));

      if (needsUpdate) {
        // Build fixtureData if fixture is selected (includes abbreviations for sportMatchData)
        const fixtureData = currentFixtureData;

        // Determine teamId for player_over_under
        const resolvedTeamId =
          isPlayerOverUnder && selectedFixtureData?.fixture && teamName
            ? // Match teamName to fixture teams to get teamId
              selectedFixtureData.fixture.homeTeamName === teamName
              ? selectedFixtureData.fixture.homeTeamId
              : selectedFixtureData.fixture.awayTeamName === teamName
                ? selectedFixtureData.fixture.awayTeamId
                : teamId || selectedFixtureData.fixture.homeTeamId
            : teamId;

        const baseGradingData: any = {
          ...values.options[0].grading_data,
          threshold,
          fixtureData,
        };

        if (isPlayerOverUnder) {
          baseGradingData.playerId = playerId;
          baseGradingData.playerName = playerName;
          baseGradingData.playerPosition = playerPosition;
          baseGradingData.teamId = resolvedTeamId;
          baseGradingData.teamName = teamName;
          baseGradingData.teamShortcode = teamShortcode;
          // Remove team-only fields if switching from team to player
          if (!isTeamOverUnder) {
            // Keep team fields for player_over_under
          }
        } else if (isTeamOverUnder) {
          baseGradingData.teamId = teamId;
          baseGradingData.teamName = teamName;
          baseGradingData.teamShortcode = teamShortcode;
          // Remove player fields if switching from player to team
          delete baseGradingData.playerId;
          delete baseGradingData.playerName;
          delete baseGradingData.playerPosition;
        } else {
          // Legacy over_under type - default to player
          baseGradingData.playerId = playerId;
          baseGradingData.playerName = playerName;
          baseGradingData.playerPosition = playerPosition;
          baseGradingData.teamId = resolvedTeamId;
          baseGradingData.teamName = teamName;
          baseGradingData.teamShortcode = teamShortcode;
        }

        setFieldValue('options.0.grading_data', {
          ...baseGradingData,
          comparison: 'over',
        });
        setFieldValue('options.1.grading_data', {
          ...baseGradingData,
          comparison: 'under',
        });

        // Sync team logo to option images for team over/under (frontend displays via option_image)
        if (isTeamOverUnder && teamLogoUrl) {
          setFieldValue('options.0.option_image_en', teamLogoUrl);
          setFieldValue('options.0.option_image_fr', teamLogoUrl);
          setFieldValue('options.1.option_image_en', teamLogoUrl);
          setFieldValue('options.1.option_image_fr', teamLogoUrl);
        }

        // Sync jersey to option images for player over/under (frontend displays via option_third_image/option_image)
        const jerseyUrl = values.question_image_en;
        if ((isPlayerOverUnder || values.question_type === 'over_under') && jerseyUrl) {
          const jerseyFr = values.question_image_fr || jerseyUrl;
          setFieldValue('options.0.option_image_en', jerseyUrl);
          setFieldValue('options.0.option_image_fr', jerseyFr);
          setFieldValue('options.0.third_image_en', jerseyUrl);
          setFieldValue('options.0.third_image_fr', jerseyFr);
          setFieldValue('options.1.option_image_en', jerseyUrl);
          setFieldValue('options.1.option_image_fr', jerseyFr);
          setFieldValue('options.1.third_image_en', jerseyUrl);
          setFieldValue('options.1.third_image_fr', jerseyFr);
        }
      }
    }

    prevQuestionTypeRef.current = currentType;
  }, [
    values.question_type,
    values.question_image_en,
    values.question_image_fr,
    threshold,
    playerId,
    playerName,
    playerPosition,
    teamId,
    teamName,
    teamShortcode,
    teamLogoUrl,
    // Note: values.options intentionally excluded - we update it in this effect, and including it causes infinite loops
    setFieldValue,
    isOverUnderType,
    isPlayerOverUnder,
    isTeamOverUnder,
    selectedFixtureData,
    teamMap,
  ]);

  return null;
}
